import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { SET_MESSAGE } from "../../Actions/Types";
import { useTranslation } from "react-i18next";
import { disable2FAApi } from "../../Api/TwoFactorAuthAPI";

// Props for the modal
interface Disable2FAModalProps {
  show: boolean;
  onHide: () => void;
  onDisable: (otp: string) => void; // callback after successful disable
  user: () => void; // callback to refresh user info
}

const Disable2FAModal: React.FC<Disable2FAModalProps> = ({
  show,
  onHide,
  onDisable,
  user,
}) => {
  const dispatch = useDispatch();
  const [otp, setOtp] = useState<string>("");

  const { t } = useTranslation();
  const { accessToken } = useSelector((store: StoreProptypes) => store);

  /**
   * Handle disable 2FA submission
   * Accepts 6-digit OTP or 16-character recovery code
   */
  const handleSubmit = async () => {
    const trimmedCode = otp.trim();
    const isValidOTP = /^\d{6}$/.test(trimmedCode);
    const isValidRecovery = /^[a-zA-Z0-9]{8,24}$/.test(trimmedCode);
    if (!isValidOTP && !isValidRecovery) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t(
            "Please enter a valid 6-digit OTP or a valid recovery code."
          ),
          toast_type: "danger",
        },
      });
      return;   
    }

    // Optional callback for parent if needed
    onDisable(trimmedCode);

    try {
      const response = await disable2FAApi({
        token: accessToken,
        code: trimmedCode,
      });

      if (response?.status) {
        dispatch({
          type: SET_MESSAGE,
          payload: {
            toast_message: response.message || t("2FA disabled successfully."),
            toast_type: "success",
          },
        });

        user(); // Refresh user info
        setOtp(""); // Clear input
        onHide(); // Close modal
      } else {
        dispatch({
          type: SET_MESSAGE,
          payload: {
            toast_message: t("Verification failed. Please try again."),
            toast_type: "danger",
          },
        });
      }
    } catch (error) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("Error disabling 2FA. Please try again."),
          toast_type: "danger",
        },
      });
      user(); // Still try refreshing state
    }
  };

  return (
    <Modal size="lg" show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t("Disable two-factor authentication")}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p>
          {t(
            "Are you sure you want to invalidate your one-time password authentication and WebAuthn devices? Enter your verification code or recovery code to continue. This action cannot be undone."
          )}
        </p>

        <Form.Group controlId="formOtp">
          <Form.Label>{t("Verification Code or Recovery Code")}</Form.Label>
          <Form.Control
            type="text"
            placeholder={t("Enter 6-digit OTP or recovery code")}
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            maxLength={24}
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t("Cancel")}
        </Button>
        <Button variant="danger" onClick={handleSubmit}>
          {t("Disable two-factor authentication")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default Disable2FAModal;
